import { JobFunction } from '@atlas/types'
import { FieldShell, SectionCard } from '@atlas/ui-components'
import { Combobox, Input, Switch } from '@atlas/ui-primitives'
import { ExternalHtml } from '@/components/work-orders/ExternalHtml'
import { ExternalSection, sourceName } from '@/components/work-orders/ExternalSection'
import { MoneyField } from '@/components/work-orders/MoneyField'
import { cleanDrafts, OriginalCodesEditor, OriginalCodesList } from '@/components/work-orders/OriginalCodes'
import type { WorkOrder } from '@/domain/work-order.types'
import { activePms, userOptions, withJobFunction } from '@/lib/option-order'
import type { UserDTO } from '@/services/user.service'
import { FormCard } from './form-layout'
import {
	type FormErrors,
	LOCKED_NOTE,
	type LockedFields,
	NO_LOCKED_FIELDS,
	parentOptions,
	type WorkOrderFormState,
} from './form-model'

// The work order form's codes, delivery and money sections, and what an external system says
// about the order: the cards that hold the fields a work order from Workfront also carries.

const inputClass =
	'w-full rounded-ds-md border border-ds-border bg-ds-background px-3 py-2 text-ds-base text-ds-text placeholder:text-ds-text-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50'

type Set = <K extends keyof WorkOrderFormState>(key: K, value: WorkOrderFormState[K]) => void

/** The original codes: rows to edit, or the list with its Vault links when the sync owns them. */
function OriginalCodesField({
	form,
	set,
	error,
	isLocked,
}: {
	form: WorkOrderFormState
	set: Set
	error?: string
	isLocked: boolean
}) {
	return (
		<FieldShell label="Original Veeva codes" span={3} description={isLocked ? LOCKED_NOTE : undefined}>
			{isLocked ? (
				<OriginalCodesList codes={cleanDrafts(form.originalCodes)} />
			) : (
				<OriginalCodesEditor
					value={form.originalCodes}
					onChange={(codes) => set('originalCodes', codes)}
					error={error}
				/>
			)}
		</FieldShell>
	)
}

/**
 * Codes & delivery — the original codes, the target Veeva code, the expected publishing date,
 * due date+time, parent order, assignee, PM, Content Factory.
 */
export function CodesCard({
	form,
	set,
	orders,
	users,
	pms: activePmUsers,
	errors,
	locked = NO_LOCKED_FIELDS,
}: {
	form: WorkOrderFormState
	set: Set
	orders: WorkOrder[]
	users: UserDTO[]
	/** Every active PM, from `GET /users/active-pms`. */
	pms: UserDTO[]
	errors: FormErrors
	/** Fields shown but not editable, each noted as changed in Workfront. */
	locked?: LockedFields
}) {
	const designers = userOptions(withJobFunction(users, JobFunction.DESIGNER, form.assigneeId))
	const pms = userOptions(activePms(activePmUsers, users, form.pmUserId))
	const note = (field: keyof WorkOrderFormState) => (locked.has(field) ? LOCKED_NOTE : undefined)
	return (
		<FormCard title="Codes & Delivery">
			<OriginalCodesField form={form} set={set} error={errors.originalCodes} isLocked={locked.has('originalCodes')} />
			<FieldShell label="Target Veeva code" htmlFor="veevaCode" description={note('veevaCode')}>
				<Input
					id="veevaCode"
					disabled={locked.has('veevaCode')}
					value={form.veevaCode}
					onChange={(e) => set('veevaCode', e.target.value)}
					placeholder="VV-000000"
				/>
			</FieldShell>
			<FieldShell
				label="Expected publishing date"
				htmlFor="expectedPublishingDate"
				description={note('expectedPublishingDate')}
			>
				<input
					id="expectedPublishingDate"
					disabled={locked.has('expectedPublishingDate')}
					type="date"
					value={form.expectedPublishingDate}
					onChange={(e) => set('expectedPublishingDate', e.target.value)}
					className={inputClass}
				/>
			</FieldShell>
			<FieldShell label="Due date & time" htmlFor="dueAt" description={note('dueAt')}>
				<input
					id="dueAt"
					disabled={locked.has('dueAt')}
					type="datetime-local"
					value={form.dueAt}
					onChange={(e) => set('dueAt', e.target.value)}
					className={inputClass}
				/>
			</FieldShell>
			<FieldShell label="Parent work order" description={note('parentWorkOrderId')}>
				<Combobox
					aria-label="Parent work order"
					options={parentOptions(orders)}
					value={form.parentWorkOrderId ?? null}
					onChange={(v) => set('parentWorkOrderId', v)}
					isDisabled={locked.has('parentWorkOrderId')}
					placeholder="None…"
				/>
			</FieldShell>
			<FieldShell label="Assignee">
				<Combobox
					aria-label="Assignee"
					options={designers}
					value={form.assigneeId || null}
					onChange={(v) => set('assigneeId', v ?? '')}
					placeholder="Unassigned…"
				/>
			</FieldShell>
			<FieldShell label="PM">
				<Combobox
					aria-label="PM"
					options={pms}
					value={form.pmUserId || null}
					onChange={(v) => set('pmUserId', v ?? '')}
					placeholder="No PM…"
				/>
			</FieldShell>
			<FieldShell label="Content factory">
				<Switch isSelected={form.isContentFactory} onChange={(v) => set('isContentFactory', v)}>
					<span className="text-ds-base text-ds-text">
						{form.isContentFactory ? 'Content Factory work' : 'Outside the Content Factory'}
					</span>
				</Switch>
			</FieldShell>
		</FormCard>
	)
}

/**
 * Costs — budget, planned and final cost, each an amount and a currency (USD until another is
 * chosen), all optional. A parent's planned and final cost are set in the external system, so on a parent
 * they show but cannot be changed.
 */
export function CostsFormCard({
	form,
	set,
	errors,
	isParent,
	source,
	locked = NO_LOCKED_FIELDS,
}: {
	form: WorkOrderFormState
	set: Set
	errors: FormErrors
	isParent: boolean
	/** The external system a parent's costs come from; null on the New form. */
	source: string | null
	/** Fields shown but not editable, each noted as changed in Workfront. */
	locked?: LockedFields
}) {
	const parentNote = isParent ? `Set in ${source ? sourceName(source) : 'the external system'} for a parent` : undefined
	const note = (field: 'budget' | 'plannedCost' | 'finalCost') =>
		locked.has(field) ? LOCKED_NOTE : field === 'budget' ? undefined : parentNote
	const isLocked = (field: 'budget' | 'plannedCost' | 'finalCost') =>
		locked.has(field) || (field !== 'budget' && isParent)
	return (
		<FormCard title="Costs">
			<FieldShell label="Budget" error={errors.budget} description={note('budget')}>
				<MoneyField
					label="Budget"
					value={form.budget}
					onChange={(v) => set('budget', v)}
					isDisabled={isLocked('budget')}
				/>
			</FieldShell>
			<FieldShell label="Planned cost" error={errors.plannedCost} description={note('plannedCost')}>
				<MoneyField
					label="Planned cost"
					value={form.plannedCost}
					onChange={(v) => set('plannedCost', v)}
					isDisabled={isLocked('plannedCost')}
				/>
			</FieldShell>
			<FieldShell label="Final cost" error={errors.finalCost} description={note('finalCost')}>
				<MoneyField
					label="Final cost"
					value={form.finalCost}
					onChange={(v) => set('finalCost', v)}
					isDisabled={isLocked('finalCost')}
				/>
			</FieldShell>
		</FormCard>
	)
}

/**
 * What the external system says about an order that came from one, read-only: it is written by
 * the sync, never by this form. Not rendered for a manual order or on the New form.
 */
export function ExternalFormCard({ order }: { order: WorkOrder | undefined }) {
	if (!order?.externalRef) return null
	return (
		<SectionCard title="From the external system" className="p-6">
			<ExternalSection order={order} />
			<dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
				{order.externalCostStatus ? (
					<div>
						<dt className="text-ds-text-subtle">Cost status</dt>
						<dd className="text-ds-text">{order.externalCostStatus}</dd>
					</div>
				) : null}
				{order.isAdaptation !== null ? (
					<div>
						<dt className="text-ds-text-subtle">Adaptation</dt>
						<dd className="text-ds-text">{order.isAdaptation ? 'Yes' : 'No'}</dd>
					</div>
				) : null}
				{order.adaptationNeeds ? (
					<div className="col-span-2">
						<dt className="text-ds-text-subtle">Adaptation needs</dt>
						<dd>
							<ExternalHtml html={order.adaptationNeeds} />
						</dd>
					</div>
				) : null}
			</dl>
		</SectionCard>
	)
}
